import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  alpha,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { Search, Person, AutoAwesome, Edit, CloudUpload } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import LoadingAnimation from '../components/LoadingAnimation';
import { openFilePreviewOrDownload, isPdfFile } from '../utils/filePreview';
import PasswordVerificationModal from '../components/PasswordVerificationModal';
import { authService } from '../services/authService';
import { useHRAccess } from '../hooks/useHRAccess';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import CVBankTableAgGrid, { CV_STATUS_OPTIONS } from '../components/CVBankTableAgGrid';
import { usePdfViewer } from '../contexts/PdfViewerContext';
import { useFormFieldErrors, REQUIRED_FIELDS_MESSAGE } from '../hooks/useFormFieldErrors';
import FormValidationAlert from '../components/FormValidationAlert';

const CV_ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
} as const;

const MAX_CV_SIZE_MB = 10;
const MAX_CV_SIZE_BYTES = MAX_CV_SIZE_MB * 1024 * 1024;

const validateCvFile = (file: File): string | null => {
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!['.pdf', '.docx'].includes(fileExtension)) {
    return `Invalid file type. Only PDF and DOCX files are allowed. You selected: ${fileExtension || 'unknown'}`;
  }
  if (file.size > MAX_CV_SIZE_BYTES) {
    return `File size exceeds ${MAX_CV_SIZE_MB}MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`;
  }
  return null;
};

const CVBankPage: React.FC = () => {
  const { user } = useAuth();
  const { viewPdf } = usePdfViewer();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [passwordVerificationOpen, setPasswordVerificationOpen] = useState(false);
  const { isVerified: passwordVerified, grantAccess } = useHRAccess();
  const [cvFiles, setCvFiles] = useState<any[]>([]);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvPage, setCvPage] = useState(1);
  const [cvTotalPages, setCvTotalPages] = useState(1);
  const [cvSearchTerm, setCvSearchTerm] = useState('');
  const [cvStatusFilter, setCvStatusFilter] = useState('');
  const [cvTagFilter, setCvTagFilter] = useState('');
  const [cvLocationFilter, setCvLocationFilter] = useState('');
  const [cvExperienceFilter, setCvExperienceFilter] = useState('');
  const [cvUploadedFromFilter, setCvUploadedFromFilter] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadCandidateProfileId, setUploadCandidateProfileId] = useState('');
  const [uploadRecruiterNotes, setUploadRecruiterNotes] = useState('');
  const [candidateProfiles, setCandidateProfiles] = useState<any[]>([]);
  const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
  const [selectedCandidateProfile, setSelectedCandidateProfile] = useState<any>(null);
  const [profileDialogLoading, setProfileDialogLoading] = useState(false);
  const [profileDialogSaving, setProfileDialogSaving] = useState(false);
  const [profileTagInput, setProfileTagInput] = useState('');
  const [profileDialogForm, setProfileDialogForm] = useState({
    full_name: '',
    job_title: '',
    email: '',
    phone: '',
    location: '',
    skills: '',
    experience_years: '',
    education: '',
    summary: '',
    parsed_data: '',
    consent_given: false,
    consent_ip: '',
    data_retention_until: ''
  });
  const [profileFormData, setProfileFormData] = useState({
    full_name: '',
    job_title: '',
    email: '',
    phone: '',
    location: '',
    skills: '',
    experience_years: '',
    education: '',
    summary: '',
    consent_given: false,
    data_retention_until: ''
  });
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedCvForComments, setSelectedCvForComments] = useState<any>(null);
  const [cvComments, setCvComments] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });
  const [cvAnalyzeLoading, setCvAnalyzeLoading] = useState(false);
  const fieldErrors = useFormFieldErrors();

  const fetchCvFiles = async () => {
    try {
      setCvLoading(true);
      const params = new URLSearchParams({
        page: cvPage.toString(),
        per_page: '10',
        search: cvSearchTerm,
        status: cvStatusFilter,
        tag: cvTagFilter,
        location: cvLocationFilter,
        experience: cvExperienceFilter,
        uploaded_from: cvUploadedFromFilter
      });

      const response = await fetch(`/api/hr/cv-bank?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCvFiles(data.cvs);
        setCvTotalPages(data.pagination.pages);
      } else {
        throw new Error('Failed to fetch CV files');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load CV files', severity: 'error' });
    } finally {
      setCvLoading(false);
    }
  };

  const fetchCandidateProfiles = async () => {
    try {
      const response = await fetch('/api/hr/candidate-profiles', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCandidateProfiles(data.profiles || []);
      }
    } catch (error) {
      console.error('Failed to load candidate profiles:', error);
    }
  };

  const resetAddCandidateForm = () => {
    setSelectedFile(null);
    setUploadCandidateProfileId('');
    setUploadRecruiterNotes('');
    setCvAnalyzeLoading(false);
    setProfileFormData({
      full_name: '',
      job_title: '',
      email: '',
      phone: '',
      location: '',
      skills: '',
      experience_years: '',
      education: '',
      summary: '',
      consent_given: false,
      data_retention_until: ''
    });
    fieldErrors.clear();
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['file']);
      return;
    }

    if (!uploadCandidateProfileId) {
      if (!profileFormData.full_name.trim()) {
        fieldErrors.show('Full name is required when creating a new candidate.', ['full_name']);
        return;
      }
    }

    const validationError = validateCvFile(selectedFile);
    if (validationError) {
      fieldErrors.show(validationError, ['file']);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (uploadCandidateProfileId) {
        formData.append('candidate_profile_id', uploadCandidateProfileId);
      } else {
        const expRaw = profileFormData.experience_years.trim();
        let experienceYears: number | null = null;
        if (expRaw) {
          const n = parseInt(expRaw, 10);
          experienceYears = Number.isNaN(n) ? null : n;
        }
        formData.append(
          'profile',
          JSON.stringify({
            full_name: profileFormData.full_name.trim() || null,
            job_title: profileFormData.job_title.trim() || null,
            email: profileFormData.email.trim() || null,
            phone: profileFormData.phone.trim() || null,
            location: profileFormData.location.trim() || null,
            skills: profileFormData.skills.split(',').map(s => s.trim()).filter(Boolean),
            experience_years: experienceYears,
            education: profileFormData.education.trim() || null,
            summary: profileFormData.summary.trim() || null,
            consent_given: profileFormData.consent_given,
            data_retention_until: profileFormData.data_retention_until.trim() || null
          })
        );
      }
      formData.append('custom_tags', profileFormData.skills);
      if (uploadRecruiterNotes.trim()) {
        formData.append('recruiter_notes', uploadRecruiterNotes);
      }

      const response = await fetch('/api/hr/cv-bank/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setUploadDialogOpen(false);
        resetAddCandidateForm();
        fetchCvFiles();
        fetchCandidateProfiles();
      } else {
        setSnackbar({ open: true, message: result.error || 'Upload failed', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Upload failed', severity: 'error' });
    }
  };

  const handleCvAnalyzeWithMistral = useCallback(async (fileOverride?: File | null) => {
    const file = fileOverride ?? selectedFile;
    // When analyzing a dropped/selected file for a new candidate, skip the existing-profile guard.
    if (!file || (!fileOverride && uploadCandidateProfileId)) return;

    const validationError = validateCvFile(file);
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }

    setCvAnalyzeLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('/api/hr/cv-bank/analyze', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      const result = await response.json();

      if (!response.ok) {
        setSnackbar({
          open: true,
          message: result.error || 'Could not analyze CV',
          severity: 'error'
        });
        return;
      }

      const p = result.profile;
      if (!p) {
        setSnackbar({ open: true, message: 'No profile data returned', severity: 'error' });
        return;
      }

      const extractedSkills = Array.isArray(p.skills)
        ? p.skills.map((tag: string) => String(tag).trim()).filter(Boolean)
        : typeof p.skills === 'string'
          ? p.skills.split(',').map((tag: string) => tag.trim()).filter(Boolean)
          : [];
      const suggestedTags = Array.isArray(p.suggested_tags)
        ? p.suggested_tags.map((tag: string) => String(tag).trim()).filter(Boolean)
        : [];
      const mergedSkills: string[] = [];
      const seen = new Set<string>();
      [...extractedSkills, ...suggestedTags].forEach((tag) => {
        const key = tag.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          mergedSkills.push(tag);
        }
      });

      setProfileFormData((prev) => ({
        ...prev,
        full_name: p.full_name || '',
        job_title: p.job_title || '',
        email: p.email || '',
        phone: p.phone || '',
        location: p.location || '',
        skills: mergedSkills.join(', '),
        experience_years:
          p.experience_years != null && p.experience_years !== ''
            ? String(p.experience_years)
            : '',
        education: p.education || '',
        summary: p.summary || ''
      }));

      setSnackbar({
        open: true,
        message: 'Form prefilled from CV (Mistral). Review all fields before saving.',
        severity: 'info'
      });
    } catch {
      setSnackbar({ open: true, message: 'Could not analyze CV', severity: 'error' });
    } finally {
      setCvAnalyzeLoading(false);
    }
  }, [selectedFile, uploadCandidateProfileId]);

  const acceptCvFileAndPrefill = useCallback((file: File) => {
    const validationError = validateCvFile(file);
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }

    // New-candidate flow so AI can prefill profile fields.
    setUploadCandidateProfileId('');
    setUploadRecruiterNotes('');
    setSelectedFile(file);
    setUploadDialogOpen(true);
    void handleCvAnalyzeWithMistral(file);
  }, [handleCvAnalyzeWithMistral]);

  const onCvFileDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    acceptCvFileAndPrefill(file);
  }, [acceptCvFileAndPrefill]);

  const onCvFileDropRejected = useCallback(() => {
    setSnackbar({
      open: true,
      message: `Invalid file. Only PDF and DOCX up to ${MAX_CV_SIZE_MB}MB are allowed.`,
      severity: 'error',
    });
  }, []);

  const {
    getRootProps: getPageDropProps,
    getInputProps: getPageDropInputProps,
    isDragActive: isPageDragActive,
  } = useDropzone({
    onDrop: onCvFileDrop,
    onDropRejected: onCvFileDropRejected,
    accept: CV_ACCEPT,
    multiple: false,
    maxSize: MAX_CV_SIZE_BYTES,
    disabled: !passwordVerified || uploadDialogOpen || cvAnalyzeLoading,
  });

  const handleUpdateCvStatus = async (cvId: number, status: string, customTags: string[], recruiterNotes: string) => {
    const previousFiles = cvFiles;
    setCvFiles((prev) => prev.map((cv) => (cv.id === cvId ? { ...cv, status } : cv)));

    try {
      const response = await fetch(`/api/hr/cv-bank/${cvId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          status,
          custom_tags: customTags,
          recruiter_notes: recruiterNotes
        })
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'CV updated successfully', severity: 'success' });
      } else {
        setCvFiles(previousFiles);
        throw new Error('Failed to update CV');
      }
    } catch (error) {
      setCvFiles(previousFiles);
      setSnackbar({ open: true, message: 'Failed to update CV', severity: 'error' });
    }
  };

  const handleUpdateCvRating = async (cvId: number, rating: number | null) => {
    const previousFiles = cvFiles;
    setCvFiles((prev) => prev.map((cv) => (cv.id === cvId ? { ...cv, rating } : cv)));

    try {
      const response = await fetch(`/api/hr/cv-bank/${cvId}/rating`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ rating })
      });

      if (!response.ok) {
        setCvFiles(previousFiles);
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Failed to update rating');
      }
    } catch (error) {
      setCvFiles(previousFiles);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update rating',
        severity: 'error'
      });
    }
  };

  const handleSaveComments = async () => {
    if (!selectedCvForComments) return;

    try {
      const response = await fetch(`/api/hr/cv-bank/${selectedCvForComments.id}/comments`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          comments: cvComments
        })
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Comments saved successfully', severity: 'success' });
        setCommentsDialogOpen(false);
        setSelectedCvForComments(null);
        setCvComments('');
        fetchCvFiles();
      } else {
        throw new Error('Failed to save comments');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save comments', severity: 'error' });
    }
  };

  const handleOpenCommentsDialog = (cv: any) => {
    setSelectedCvForComments(cv);
    setCvComments(cv.comments || '');
    setCommentsDialogOpen(true);
  };

  const handleFileDownload = async (cvId: number, filename: string) => {
    try {
      const response = await fetch(`/api/hr/cv-bank/${cvId}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      if (isPdfFile(filename, blob.type)) {
        viewPdf(blob, filename, blob.type, {
          enableAnnotations: true,
          title: filename,
          onSaveAnnotatedPdf: async (annotatedBlob) => {
            const formData = new FormData();
            formData.append('file', annotatedBlob, filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
            const saveResponse = await fetch(`/api/hr/cv-bank/${cvId}/content`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              },
              body: formData
            });
            const result = await saveResponse.json().catch(() => ({}));
            if (!saveResponse.ok) {
              throw new Error(result.error || 'Failed to save annotated CV');
            }
            // Refresh size/updated metadata quietly; viewer shows Saved status.
            fetchCvFiles();
          }
        });
        return;
      }

      openFilePreviewOrDownload(blob, filename);
    } catch (error) {
      setSnackbar({ open: true, message: 'Download failed', severity: 'error' });
    }
  };

  const handleOpenCandidateProfileDialog = async (profileId?: number) => {
    if (!profileId) {
      setSnackbar({ open: true, message: 'No candidate profile linked to this CV', severity: 'info' });
      return;
    }

    try {
      setProfileDialogLoading(true);
      const response = await fetch('/api/hr/candidate-profiles?per_page=500', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load candidate profile');
      }

      const data = await response.json();
      const profile = (data.profiles || []).find((p: any) => p.id === profileId);
      if (!profile) {
        setSnackbar({ open: true, message: 'Candidate profile not found', severity: 'error' });
        return;
      }

      setSelectedCandidateProfile(profile);
      setProfileTagInput('');
      setProfileDialogForm({
        full_name: profile.full_name || '',
        job_title: profile.job_title || '',
        email: profile.email || '',
        phone: profile.phone || '',
        location: profile.location || '',
        skills: Array.isArray(profile.skills) ? profile.skills.join(', ') : '',
        experience_years:
          profile.experience_years != null && profile.experience_years !== ''
            ? String(profile.experience_years)
            : '',
        education: profile.education || '',
        summary: profile.summary || '',
        parsed_data: profile.parsed_data ? JSON.stringify(profile.parsed_data, null, 2) : '',
        consent_given: Boolean(profile.consent_given),
        consent_ip: profile.consent_ip || '',
        data_retention_until: profile.data_retention_until || ''
      });
      setEditProfileDialogOpen(true);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load candidate profile', severity: 'error' });
    } finally {
      setProfileDialogLoading(false);
    }
  };

  const getProfileSkillTags = () =>
    profileDialogForm.skills
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

  const addProfileSkillTag = (rawTag: string) => {
    const cleaned = rawTag.trim();
    if (!cleaned) return;

    const tags = getProfileSkillTags();
    if (tags.some((tag) => tag.toLowerCase() === cleaned.toLowerCase())) {
      return;
    }

    setProfileDialogForm({
      ...profileDialogForm,
      skills: [...tags, cleaned].join(', ')
    });
  };

  const removeProfileSkillTag = (tagToRemove: string) => {
    const updated = getProfileSkillTags().filter(
      (tag) => tag.toLowerCase() !== tagToRemove.toLowerCase()
    );
    setProfileDialogForm({
      ...profileDialogForm,
      skills: updated.join(', ')
    });
  };

  const handleSaveCandidateProfile = async () => {
    if (!selectedCandidateProfile?.id) return;
    if (!profileDialogForm.full_name.trim()) {
      fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['full_name']);
      return;
    }

    let parsedDataValue: any = null;
    if (profileDialogForm.parsed_data.trim()) {
      try {
        parsedDataValue = JSON.parse(profileDialogForm.parsed_data);
      } catch {
        fieldErrors.show('Parsed data must be valid JSON before saving.');
        return;
      }
    }

    setProfileDialogSaving(true);
    try {
      const expRaw = profileDialogForm.experience_years.trim();
      let experienceYears: number | null = null;
      if (expRaw) {
        const n = parseInt(expRaw, 10);
        experienceYears = Number.isNaN(n) ? null : n;
      }

      const response = await fetch(`/api/hr/candidate-profiles/${selectedCandidateProfile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          full_name: profileDialogForm.full_name.trim() || null,
          job_title: profileDialogForm.job_title.trim() || null,
          email: profileDialogForm.email.trim() || null,
          phone: profileDialogForm.phone.trim() || null,
          location: profileDialogForm.location.trim() || null,
          skills: profileDialogForm.skills.split(',').map((skill) => skill.trim()).filter(Boolean),
          experience_years: experienceYears,
          education: profileDialogForm.education.trim() || null,
          summary: profileDialogForm.summary.trim() || null,
          parsed_data: parsedDataValue,
          consent_given: profileDialogForm.consent_given,
          consent_ip: profileDialogForm.consent_ip.trim() || null,
          data_retention_until: profileDialogForm.data_retention_until.trim() || null
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to update candidate profile');
      }

      setSnackbar({ open: true, message: 'Candidate profile updated successfully', severity: 'success' });
      setEditProfileDialogOpen(false);
      setSelectedCandidateProfile(null);
      await Promise.all([fetchCvFiles(), fetchCandidateProfiles()]);
    } catch (error: any) {
      fieldErrors.show(error?.message || 'Failed to update candidate profile');
    } finally {
      setProfileDialogSaving(false);
    }
  };

  const handleFileDelete = async (cvId: number) => {
    if (!window.confirm('Are you sure you want to delete this CV?')) return;

    try {
      const response = await fetch(`/api/hr/cv-bank/${cvId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'CV deleted successfully', severity: 'success' });
        fetchCvFiles();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    }
  };

  useEffect(() => {
    // Check if password verification is needed
    if (!passwordVerified) {
      setPasswordVerificationOpen(true);
    }
  }, [passwordVerified]);

  useEffect(() => {
    if (passwordVerified) {
      fetchCvFiles();
      fetchCandidateProfiles();
    }
  }, [cvPage, cvSearchTerm, cvStatusFilter, cvTagFilter, cvLocationFilter, cvExperienceFilter, cvUploadedFromFilter, passwordVerified]);

  const handlePasswordVerification = async (password: string) => {
    try {
      const response = await authService.verifyPassword(password);
      return response.data.verified;
    } catch (error) {
      return false;
    }
  };

  const handlePasswordVerified = () => {
    grantAccess();
    setPasswordVerificationOpen(false);
  };

  if (!passwordVerified) {
    return (
      <PasswordVerificationModal
        open={passwordVerificationOpen}
        onClose={() => setPasswordVerificationOpen(false)}
        onVerify={handlePasswordVerification}
        onVerified={handlePasswordVerified}
        title="Access HR Management"
        description="Please verify your password to access HR management features."
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          CV Bank
        </Typography>
        <Button
          variant="contained"
          startIcon={<Person />}
          onClick={() => {
            resetAddCandidateForm();
            setUploadDialogOpen(true);
          }}
        >
          Add candidate & CV
        </Button>
      </Box>

      <Box
        {...getPageDropProps()}
        sx={{
          mb: 3,
          px: 3,
          py: 3,
          borderRadius: 2,
          border: `2px dashed ${
            isPageDragActive ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.25)
          }`,
          bgcolor: isPageDragActive
            ? alpha(theme.palette.primary.main, 0.1)
            : alpha(theme.palette.primary.main, 0.03),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          cursor: cvAnalyzeLoading ? 'wait' : 'pointer',
          outline: 'none',
          transition: 'border-color 0.15s, background-color 0.15s',
        }}
      >
        <input {...getPageDropInputProps()} />
        {cvAnalyzeLoading ? (
          <CircularProgress size={28} />
        ) : (
          <CloudUpload
            sx={{ fontSize: 32, color: isPageDragActive ? 'primary.main' : 'action.active' }}
          />
        )}
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }} color={isPageDragActive ? 'primary.main' : 'text.primary'}>
            {isPageDragActive
              ? 'Drop CV to add candidate'
              : cvAnalyzeLoading
                ? 'Extracting CV with AI…'
                : 'Drag & drop a CV here, or click to browse'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            PDF or DOCX up to {MAX_CV_SIZE_MB}MB — opens the form and runs AI extraction automatically
          </Typography>
        </Box>
      </Box>

      {/* CV Search and Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search CVs"
          variant="outlined"
          value={cvSearchTerm}
          onChange={(e) => setCvSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
          placeholder="Search by name, email, skills, notes..."
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={cvStatusFilter}
            onChange={(e) => setCvStatusFilter(e.target.value)}
            label="Status"
            MenuProps={getZoomCompensatedSelectMenuProps()}
          >
            <MenuItem value="">All</MenuItem>
            {CV_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Filter by tag"
          variant="outlined"
          value={cvTagFilter}
          onChange={(e) => setCvTagFilter(e.target.value)}
          sx={{ minWidth: 150 }}
          placeholder="e.g., pyt, senior"
        />
        <TextField
          label="Location"
          variant="outlined"
          value={cvLocationFilter}
          onChange={(e) => setCvLocationFilter(e.target.value)}
          sx={{ minWidth: 150 }}
          placeholder="e.g., Paris, Remote"
        />
        <TextField
          label="Min Experience (years)"
          variant="outlined"
          type="number"
          value={cvExperienceFilter}
          onChange={(e) => setCvExperienceFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Uploaded from"
          variant="outlined"
          type="date"
          value={cvUploadedFromFilter}
          onChange={(e) => setCvUploadedFromFilter(e.target.value)}
          sx={{ minWidth: 180 }}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          onClick={() => {
            setCvSearchTerm('');
            setCvStatusFilter('');
            setCvTagFilter('');
            setCvLocationFilter('');
            setCvExperienceFilter('');
            setCvUploadedFromFilter('');
            setCvPage(1);
          }}
          sx={{ minWidth: 'auto' }}
        >
          Clear Filters
        </Button>
      </Box>

      {/* CV Files Table */}
      {cvLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LoadingAnimation />
        </Box>
      ) : (
        <>
          <CVBankTableAgGrid
            cvs={cvFiles}
            onStatusChange={handleUpdateCvStatus}
            onRatingChange={handleUpdateCvRating}
            onDownload={handleFileDownload}
            onComments={handleOpenCommentsDialog}
            onEditProfile={handleOpenCandidateProfileDialog}
            onDelete={handleFileDelete}
            profileDialogLoading={profileDialogLoading}
          />

          {/* CV Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={cvTotalPages}
              page={cvPage}
              onChange={(event, value) => setCvPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}

      {/* Add candidate + CV (single flow) */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          resetAddCandidateForm();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            px: 3,
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <Person sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Add candidate & CV
          </Typography>
        </DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Existing candidate (optional)</InputLabel>
              <Select
                value={uploadCandidateProfileId}
                onChange={(e) => setUploadCandidateProfileId(e.target.value)}
                label="Existing candidate (optional)"
                MenuProps={getZoomCompensatedSelectMenuProps()}
              >
                <MenuItem value="">New candidate — fill profile below</MenuItem>
                {candidateProfiles.map((profile) => (
                  <MenuItem key={profile.id} value={String(profile.id)}>
                    {profile.full_name || profile.email || `Profile ${profile.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {uploadCandidateProfileId ? (
              <Typography variant="body2" color="text.secondary">
                Profile fields below are ignored; the CV is attached to the selected candidate.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                CV was set from the page drop zone (or select a file below). Use Prefill from CV (AI) if needed.
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<Person />}
                color={fieldErrors.invalid('file') ? 'error' : 'primary'}
              >
                Select CV file
                <input
                  type="file"
                  hidden
                  accept=".pdf,.docx"
                  onChange={(e) => {
                    fieldErrors.clearField('file');
                    setSelectedFile(e.target.files?.[0] || null);
                  }}
                />
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => void handleCvAnalyzeWithMistral()}
                disabled={!selectedFile || Boolean(uploadCandidateProfileId) || cvAnalyzeLoading}
                startIcon={
                  cvAnalyzeLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <AutoAwesome />
                  )
                }
              >
                {cvAnalyzeLoading ? 'Extracting…' : 'Prefill from CV (AI)'}
              </Button>
            </Box>
            {selectedFile && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Allowed: PDF, DOCX files up to {MAX_CV_SIZE_MB}MB
                </Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Full Name"
                  required
                  value={profileFormData.full_name}
                  error={fieldErrors.invalid('full_name')}
                  onChange={(e) => {
                    fieldErrors.clearField('full_name');
                    setProfileFormData({ ...profileFormData, full_name: e.target.value });
                  }}
                  fullWidth
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Job Title"
                  value={profileFormData.job_title}
                  onChange={(e) => setProfileFormData({ ...profileFormData, job_title: e.target.value })}
                  fullWidth
                  placeholder="e.g., Senior Backend Engineer"
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={profileFormData.email}
                  onChange={(e) => setProfileFormData({ ...profileFormData, email: e.target.value })}
                  fullWidth
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  value={profileFormData.phone}
                  onChange={(e) => setProfileFormData({ ...profileFormData, phone: e.target.value })}
                  fullWidth
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Location"
                  value={profileFormData.location}
                  onChange={(e) => setProfileFormData({ ...profileFormData, location: e.target.value })}
                  fullWidth
                  placeholder="e.g., Paris, France"
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Experience (years)"
                  type="number"
                  value={profileFormData.experience_years}
                  onChange={(e) => setProfileFormData({ ...profileFormData, experience_years: e.target.value })}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data Retention Until"
                  type="date"
                  value={profileFormData.data_retention_until}
                  onChange={(e) => setProfileFormData({ ...profileFormData, data_retention_until: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="GDPR compliance - when to delete data"
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Skills (comma-separated)"
                  value={profileFormData.skills}
                  onChange={(e) => setProfileFormData({ ...profileFormData, skills: e.target.value })}
                  fullWidth
                  placeholder="e.g., Python, React, Node.js, SQL"
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Education"
                  value={profileFormData.education}
                  onChange={(e) => setProfileFormData({ ...profileFormData, education: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="e.g., Master's in Computer Science, University of..."
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Summary"
                  value={profileFormData.summary}
                  onChange={(e) => setProfileFormData({ ...profileFormData, summary: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Brief professional summary..."
                  disabled={Boolean(uploadCandidateProfileId)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" disabled={Boolean(uploadCandidateProfileId)}>
                  <Typography variant="body2" gutterBottom>
                    GDPR Consent Given
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      checked={profileFormData.consent_given}
                      onChange={(e) => setProfileFormData({ ...profileFormData, consent_given: e.target.checked })}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Candidate has given consent for data processing
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Recruiter Notes"
              value={uploadRecruiterNotes}
              onChange={(e) => setUploadRecruiterNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Internal notes about this candidate..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              resetAddCandidateForm();
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFileUpload}
            variant="contained"
            disabled={cvAnalyzeLoading}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Save profile & upload CV
          </Button>
        </DialogActions>
      </Dialog>

      {/* Candidate Profile Dialog */}
      <Dialog
        open={editProfileDialogOpen}
        onClose={() => {
          setEditProfileDialogOpen(false);
          setSelectedCandidateProfile(null);
          setProfileTagInput('');
          fieldErrors.clear();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.12)}`
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Candidate Profile Editor
        </DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Full Name"
                  value={profileDialogForm.full_name}
                  error={fieldErrors.invalid('full_name')}
                  onChange={(e) => {
                    fieldErrors.clearField('full_name');
                    setProfileDialogForm({ ...profileDialogForm, full_name: e.target.value });
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Job Title"
                  value={profileDialogForm.job_title}
                  onChange={(e) => setProfileDialogForm({ ...profileDialogForm, job_title: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={profileDialogForm.email}
                  onChange={(e) => setProfileDialogForm({ ...profileDialogForm, email: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  value={profileDialogForm.phone}
                  onChange={(e) => setProfileDialogForm({ ...profileDialogForm, phone: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Location"
                  value={profileDialogForm.location}
                  onChange={(e) => setProfileDialogForm({ ...profileDialogForm, location: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Experience (years)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={profileDialogForm.experience_years}
                  onChange={(e) => setProfileDialogForm({ ...profileDialogForm, experience_years: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data Retention Until"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={profileDialogForm.data_retention_until}
                  onChange={(e) => setProfileDialogForm({ ...profileDialogForm, data_retention_until: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Skills / Tags
                  </Typography>
                  <Box
                    sx={{
                      minHeight: 44,
                      border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.25 : 0.15)}`,
                      borderRadius: 1.5,
                      p: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      alignItems: 'center'
                    }}
                  >
                    {getProfileSkillTags().map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        onDelete={() => removeProfileSkillTag(tag)}
                        variant="outlined"
                      />
                    ))}
                    {getProfileSkillTags().length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No tags yet. Type and press Enter.
                      </Typography>
                    )}
                  </Box>
                  <TextField
                    label="Add tag"
                    value={profileTagInput}
                    onChange={(e) => setProfileTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addProfileSkillTag(profileTagInput);
                        setProfileTagInput('');
                      }
                    }}
                    onBlur={() => {
                      if (profileTagInput.trim()) {
                        addProfileSkillTag(profileTagInput);
                        setProfileTagInput('');
                      }
                    }}
                    fullWidth
                    placeholder="Type a tag and press Enter"
                    helperText="Stored as comma-separated tags for AI compatibility"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Education"
                  value={profileDialogForm.education}
                  onChange={(e) => setProfileDialogForm({ ...profileDialogForm, education: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Summary"
                  value={profileDialogForm.summary}
                  onChange={(e) => setProfileDialogForm({ ...profileDialogForm, summary: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
            <Divider />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={profileDialogForm.consent_given}
                    onChange={(e) => setProfileDialogForm({ ...profileDialogForm, consent_given: e.target.checked })}
                  />
                }
                label="GDPR consent given"
              />
            </Box>
            {selectedCandidateProfile && (
              <Typography variant="caption" color="text.secondary">
                Profile #{selectedCandidateProfile.id} • Created {new Date(selectedCandidateProfile.created_at).toLocaleString()} • Last update {new Date(selectedCandidateProfile.updated_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setEditProfileDialogOpen(false);
              setSelectedCandidateProfile(null);
              setProfileTagInput('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCandidateProfile}
            disabled={profileDialogSaving}
            startIcon={profileDialogSaving ? <CircularProgress size={16} color="inherit" /> : <Edit fontSize="small" />}
          >
            Save profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog
        open={commentsDialogOpen}
        onClose={() => setCommentsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            px: 3,
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <Person sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            CV Comments - {selectedCvForComments?.candidate_name || selectedCvForComments?.original_filename}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Comments"
              multiline
              rows={6}
              value={cvComments}
              onChange={(e) => setCvComments(e.target.value)}
              fullWidth
              placeholder="Add your comments about this CV..."
              helperText="These comments will be visible to recruiters and HR staff"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => {
              setCommentsDialogOpen(false);
              setSelectedCvForComments(null);
              setCvComments('');
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveComments}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Save Comments
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CVBankPage;